/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Divider,
  Button,
  Box,
  Text,
  TextInput,
  Select,
  Checkbox,
  Textarea,
  Group,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import ModalHeader from "components/ModalHeader";
import {
  useEditAttendanceRecord,
  EditAttendanceRecordPayload,
} from "api/attendance/editAttendanceRecord";

// ——— Helpers ———

/**
 * Convert an ISO datetime string to a datetime-local input value (YYYY-MM-DDTHH:MM),
 * expressed in local time so the browser shows the correct clock time.
 */
const toDateTimeLocal = (isoStr: string | undefined | null): string => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "";
  const offsetMs = d.getTimezoneOffset() * 60_000;
  const local = new Date(d.getTime() - offsetMs);
  return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
};

/**
 * Convert a datetime-local string ("YYYY-MM-DDTHH:MM") back to an ISO string.
 * Returns undefined if the input is empty.
 */
const fromDateTimeLocal = (localStr: string): string | undefined => {
  if (!localStr) return undefined;
  return new Date(localStr).toISOString();
};

// ——— Props ———

type EditAttendanceModalProps = {
  opened: boolean;
  onClose: () => void;
  recordId: string;
  employeeName: string;
  day: string;
  // Pre-fill values from the table row
  initialCheckIn?: string | null;
  initialCheckOut?: string | null;
  initialStatus?: "Present" | "Absent";
  initialIsHalfDay?: boolean;
  initialNotes?: string;
};

// ——— Component ———

function EditAttendanceModal({
  opened,
  onClose,
  recordId,
  employeeName,
  day,
  initialCheckIn,
  initialCheckOut,
  initialStatus = "Present",
  initialIsHalfDay = false,
  initialNotes = "",
}: EditAttendanceModalProps) {
  const { mutate: editRecord, isLoading } = useEditAttendanceRecord();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // ——— Form state ———
  const [punchInTime, setPunchInTime] = useState<string>("");
  const [punchOutTime, setPunchOutTime] = useState<string>("");
  const [status, setStatus] = useState<"Present" | "Absent">(initialStatus);
  const [isHalfDay, setIsHalfDay] = useState<boolean>(initialIsHalfDay);
  const [notes, setNotes] = useState<string>(initialNotes);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form whenever the modal opens with new data
  useEffect(() => {
    if (opened) {
      setPunchInTime(toDateTimeLocal(initialCheckIn));
      setPunchOutTime(toDateTimeLocal(initialCheckOut));
      setStatus(initialStatus);
      setIsHalfDay(initialIsHalfDay);
      setNotes(initialNotes ?? "");
      setValidationError(null);
      setHasSubmitted(false);
    }
  }, [
    opened,
    initialCheckIn,
    initialCheckOut,
    initialStatus,
    initialIsHalfDay,
    initialNotes,
  ]);

  // ——— Client-side validation ———
  const validate = (): boolean => {
    if (punchInTime && punchOutTime) {
      const pIn = new Date(punchInTime);
      const pOut = new Date(punchOutTime);

      if (pIn >= pOut) {
        setValidationError("Punch-in time must be before punch-out time.");
        return false;
      }

      const sessionHours =
        (pOut.getTime() - pIn.getTime()) / (1000 * 60 * 60);
      if (sessionHours > 24) {
        setValidationError(
          "Session duration cannot exceed 24 hours. Please verify the times."
        );
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  // ——— Submit ———
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (hasSubmitted || isLoading) return;
    if (!validate()) return;

    setHasSubmitted(true);

    if (!recordId) {
      console.error("Record ID is missing");
      setHasSubmitted(false);
      return;
    }

    const payload: EditAttendanceRecordPayload = {};

    // Only include fields that differ from initial values
    const newPunchIn = fromDateTimeLocal(punchInTime);
    const newPunchOut = fromDateTimeLocal(punchOutTime);
    const existingPunchIn = initialCheckIn
      ? new Date(initialCheckIn).toISOString()
      : undefined;
    const existingPunchOut = initialCheckOut
      ? new Date(initialCheckOut).toISOString()
      : undefined;

    if (newPunchIn && newPunchIn !== existingPunchIn) {
      payload.punchInTime = newPunchIn;
    }
    // Always send punchOutTime if the field has been touched
    if (newPunchOut !== existingPunchOut) {
      payload.punchOutTime = newPunchOut;
    }

    if (status !== initialStatus) payload.status = status;
    if (isHalfDay !== initialIsHalfDay) payload.isHalfDay = isHalfDay;
    if (notes !== (initialNotes ?? "")) payload.notes = notes;

    // Nothing changed — inform user
    if (Object.keys(payload).length === 0) {
      setValidationError("No changes detected. Please modify at least one field.");
      setHasSubmitted(false);
      return;
    }

    editRecord(
      { recordId, payload },
      {
        onSuccess: () => {
          setHasSubmitted(false);
          onClose();
        },
        onError: () => {
          setHasSubmitted(false);
        },
      }
    );
  };

  const handleClose = () => {
    setHasSubmitted(false);
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="md"
      withCloseButton={false}
      transitionDuration={300}
      centered
      overlayOpacity={0.5}
    >
      <ModalHeader
        title="Edit Attendance Record"
        onClose={handleClose}
        ariaLabel="Close edit attendance modal"
      />
      <Divider mb="md" />

      <Box
        component="form"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {/* Employee info */}
        <Box>
          <Text size="xs" color="dimmed" mb={2}>
            Employee
          </Text>
          <Text size="sm" fw={500}>
            {employeeName || "—"}{" "}
            <Text component="span" size="xs" color="dimmed">
              · {day}
            </Text>
          </Text>
        </Box>

        <Divider />

        {/* Punch-in Time */}
        <TextInput
          label="Punch-in Time"
          type="datetime-local"
          value={punchInTime}
          onChange={(e) => {
            setPunchInTime(e.currentTarget.value);
            setValidationError(null);
          }}
          description="Leave blank to keep the existing punch-in time"
        />

        {/* Punch-out Time */}
        <TextInput
          label="Punch-out Time"
          type="datetime-local"
          value={punchOutTime}
          onChange={(e) => {
            setPunchOutTime(e.currentTarget.value);
            setValidationError(null);
          }}
          description="Leave blank to keep the existing punch-out time"
        />

        {/* Status */}
        <Select
          label="Status"
          value={status}
          onChange={(val) => {
            if (val === "Present" || val === "Absent") setStatus(val);
          }}
          data={[
            { value: "Present", label: "Present" },
            { value: "Absent", label: "Absent" },
          ]}
        />

        {/* Half Day */}
        <Checkbox
          label="Half Day"
          checked={isHalfDay}
          onChange={(e) => setIsHalfDay(e.currentTarget.checked)}
        />

        {/* Notes */}
        <Textarea
          label="Notes"
          placeholder="Add a note (e.g. reason for late arrival)"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
          maxRows={4}
        />

        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Info note about auto-recalculation */}
        <Alert color="blue" variant="light" style={{ fontSize: "12px" }}>
          Changing punch times will automatically recalculate working hours,
          late arrival, and overtime.
        </Alert>

        {/* Actions */}
        <Button
          type="submit"
          fullWidth
          loading={isLoading || hasSubmitted}
          color="blue"
        >
          Save Changes
        </Button>
        <Button
          variant="light"
          onClick={handleClose}
          fullWidth
          disabled={isLoading}
        >
          Cancel
        </Button>
      </Box>
    </Modal>
  );
}

export default EditAttendanceModal;
